import { TableRow } from "@carbon6/c6-frontend-design";
import type { QueryHotspotResp } from "@/api";
import { fingerprintHex, formatCount, formatMillis, formatOptional } from "./observabilityModel";

/**
 * 쿼리 hotspot 한 행이다. 계약에 SQL 원문은 없고, `query_id` fingerprint 만 있다.
 *
 * **시간 필드가 두 종류라서 행도 값을 두 묶음으로 나눠 가진다.**
 * - `windowed*` 는 이 창의 값이다. 창을 바꾸면 같이 바뀐다.
 * - `cumulative*` 는 통계 리셋 이후의 누적 게이지다. 창을 좁혀도 잘 줄어들지 않는다.
 *
 * 한 묶음으로 합치면 "평균은 2ms 인데 최대가 3초" 를 같은 구간의 값으로 읽는다. 그러면 창을
 * 좁혀도 최댓값이 줄지 않는 것을 버그로 오해한다.
 */
export class QueryHotspotTableRow extends TableRow {
  readonly queryId: string;
  /** 같은 값의 64비트 hex 다. 툴팁에만 쓴다. 파싱할 수 없으면 빈 문자열이다. */
  readonly queryIdHex: string;
  readonly windowedCalls: string;
  readonly windowedTotalMs: string;
  readonly windowedMeanMs: string;
  readonly cumulativeMinMs: string;
  readonly cumulativeMaxMs: string;
  readonly cumulativeStddevMs: string;
  readonly sharedBlocksRead: string;

  constructor(hotspot: QueryHotspotResp) {
    super(null);
    // 계약은 이 값을 문자열로 준다. 숫자로 바꾸면 2^53 위에서 자릿수를 잃는다. 식별자이므로
    // 천 단위 구분도 넣지 않는다. 사용자가 그대로 복사해 조회에 쓰는 값이다.
    this.queryId = hotspot.query_id;
    this.queryIdHex = fingerprintHex(hotspot.query_id);
    this.windowedCalls = formatOptional(hotspot.calls, formatCount);
    this.windowedTotalMs = formatOptional(hotspot.total_exec_ms, formatMillis);
    this.windowedMeanMs = formatOptional(hotspot.mean_exec_ms, formatMillis);
    this.cumulativeMinMs = formatOptional(hotspot.min_exec_ms, formatMillis);
    this.cumulativeMaxMs = formatOptional(hotspot.max_exec_ms, formatMillis);
    this.cumulativeStddevMs = formatOptional(hotspot.stddev_exec_ms, formatMillis);
    this.sharedBlocksRead = formatOptional(hotspot.shared_blocks_read, formatCount);
  }

  getRowId(): string {
    return this.queryId;
  }

  // 서버가 이미 총 시간 상위 20건으로 줄였다.
  canShow(): boolean {
    return true;
  }

  canSelect(): boolean {
    return false;
  }
}

export function createQueryHotspotRows(
  hotspots: readonly QueryHotspotResp[],
): QueryHotspotTableRow[] {
  return hotspots.map((hotspot) => new QueryHotspotTableRow(hotspot));
}
